import {noop, omit} from 'es-toolkit'

import * as http from '../services/http'

import {retryPromise} from './promise'

export type HttpFetchOptions = http.HttpOptions &
  Omit<RequestInit, 'body' | 'headers' | 'method'> & {
    body?: object
  }

export type HttpJsonResponse = {
  json: any // eslint-disable-line @typescript-eslint/no-explicit-any
  http: Response
}

export type RetryOptions = {
  times: number
  delay?: number
  shouldRetry?: (error: Error) => boolean
  onRetry?: (error: Error) => void
}

const getPathName = (url: string) => {
  try {
    return new URL(url).pathname
  } catch {
    return 'unknown path'
  }
}

export class HttpStatusError extends Error {
  public response: Response

  public body: string | null

  constructor(response: Response, body: string | null) {
    super(`Response returned with unexpected status ${response.status} ${response.statusText}.`)
    this.name = `HttpStatusError on ${getPathName(response.url)}`
    this.response = response
    this.body = body
  }
}

export class HttpFetchBlobStatusError extends Error {
  public response: Response

  public body: string | null

  constructor(response: Response, body: string | null) {
    super(`Response returned with unexpected status ${response.status} ${response.statusText}.`)
    this.name = `HttpFetchBlobStatusError on ${getPathName(response.url)}`
    this.response = response
    this.body = body
  }
}

export class HttpBodyParseError extends Error {
  public response: Response

  public body: string | null

  constructor(cause: string, response: Response, body: string | null) {
    super(`Cannot parse response: ${cause}`)
    this.name = `HttpBodyParseError on ${getPathName(response.url)}`
    this.response = response
    this.body = body
  }
}

const getBodyTextIfPossible = async (response: Response): Promise<string | null> => {
  try {
    return await response.text()
  } catch {
    return null
  }
}

const processResponse = async (response: Response): Promise<HttpJsonResponse> => {
  if (response.ok === false) {
    throw new HttpStatusError(response, await getBodyTextIfPossible(response))
  }
  // 204 no content
  if (response.status === 204) {
    return {
      json: {},
      http: response,
    }
  }
  try {
    return {
      json: await response.json(),
      http: response,
    }
  } catch (error) {
    throw new HttpBodyParseError(
      (error as Error)?.message,
      response,
      await getBodyTextIfPossible(response)
    )
  }
}

export const jsonFetch = async (
  url: string,
  options: HttpFetchOptions = {},
  retryOptions?: RetryOptions
): Promise<HttpJsonResponse> => {
  const {times = 0, delay = 0, shouldRetry = () => true, onRetry = noop} = retryOptions ?? {}

  const doFetch = async () => {
    const fetchOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      method: options.method || 'GET',
      ...omit(options, ['headers', 'method', 'body']),
    }

    if (options.body) {
      fetchOptions.body = JSON.stringify(options.body)
    }

    return processResponse(await fetch(url, fetchOptions))
  }

  const {promise} = retryPromise(doFetch, times, delay, shouldRetry, onRetry)

  return promise
}

export const fetchBlob = async (url: string, options: HttpFetchOptions = {}): Promise<Blob> => {
  const doFetch = async () => {
    const fetchOptions: RequestInit = {
      headers: {
        ...(options.headers || {}),
      },
      method: options.method || 'GET',
      ...omit(options, ['headers', 'method', 'body']),
    }

    return fetch(url, fetchOptions).then(response => {
      if (response.status === 200 && response.ok) {
        return response.blob()
      }
      throw new HttpFetchBlobStatusError(response, null)
    })
  }

  return doFetch()
}

export const upload = async (...args: Parameters<typeof http.upload>) =>
  processResponse(await http.upload(...args))

// Helper for fetch with retry to retry only for certain HTTP statuses.
export const shouldRetryStatuses =
  (...statuses: number[]) =>
  (error: Error) =>
    error instanceof TypeError ||
    error instanceof HttpBodyParseError ||
    (error instanceof HttpStatusError && statuses.includes(error.response.status))
